import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
	DATETIME_FORMAT_DEPARTURE,
	DATETIME_FORMAT_DEPARTURE_12H,
	DATETIME_FORMAT_FULL,
	DATETIME_FORMAT_FULL_12H,
	JET_ONE_SUITE_REQUEST_STATUS,
	NOTIFICATION_TYPE,
	TIME_FORMAT_12,
	TIME_FORMAT_24,
	TIME_SETTING_TYPES,
} from "@/config/constants";
import { toast } from "@/modules/toast";
import { requestJetOneSuiteAccess } from "@/stores/apis/apiJetOneSuites";
import { signIn, getUserInfo, updateUserInfo } from "../apis/apiAuth";
import { setCookie, getCookie, deleteCookie } from "cookies-next";

// Initial state
const initialState = {
	authState: false,
	user: null,
	openAuthModal: false,
	openAuthTitle: "Log In",
	openAuthMode: "LOGIN", // LOGIN / REGISTER / RESET
	status: "loading", // loading - idle,
	isPendingRegister: false,
};

export const fetchUserInfo = createAsyncThunk("auth/fetchUserInfo", async (payload = {}, { rejectWithValue }) => {
	let localUser = getCookie("ACCESS_TOKEN");
	if (localUser) {
		let userResponse = await getUserInfo();
		return userResponse;
	} else {
		rejectWithValue({ success: false, error: "Not found local user." });
	}
});

export const fetchUpdateUserInfo = createAsyncThunk("auth/fetchUpdateUserInfo", async (payload) => {
	let userResponse = await updateUserInfo(payload);
	if (userResponse) {
		toast({
			type: NOTIFICATION_TYPE.SUCCESS,
			description: "Profile Updated Successfully.",
		});
		return userResponse;
	} else {
		toast({
			type: NOTIFICATION_TYPE.ERROR,
			description: "Updated Profile Failed.",
		});
	}
});

export const fetchRequestSuiteAccess = createAsyncThunk(
	"auth/fetchRequestSuiteAccess",
	async (payload = {}, { rejectWithValue }) => {
		let requestResponse = await requestJetOneSuiteAccess(payload);
		if (requestResponse && requestResponse.success) {
			return requestResponse;
		} else {
			rejectWithValue(requestResponse.errors);
		}
	},
);

export const fetchUserLogin = createAsyncThunk("auth/fetchUserLogin", async (payload = {}, { rejectWithValue }) => {
	const { email, password, forceCloseModal = false } = payload;
	const response = await signIn({ email, password });
	if (response && response.success) {
		let result = { ...response.data };
		if (result?.data.token) {
			setCookie("ACCESS_TOKEN", result?.data.token);
		}
		if (result?.data.refresh_token) {
			setCookie("REFRESH_TOKEN", result?.data.refresh_token);
		}
		if (result.success) {
			let userResponse = await getUserInfo();
			if (userResponse) result.user = userResponse?.data;
		}
		result.forceCloseModal = forceCloseModal;
		return result;
	} else {
		toast({
			type: NOTIFICATION_TYPE.ERROR,
			description: "Please enter a correct email or password!",
		});
		rejectWithValue(response.errors);
	}
});

export const fetchUserLogout = createAsyncThunk("auth/fetchUserLogout", async () => {
	deleteCookie("ACCESS_TOKEN");
	deleteCookie("REFRESH_TOKEN");

	toast({
		type: NOTIFICATION_TYPE.SUCCESS,
		description: "You have successfully logged out!",
	});
	return true;
});

export const authSlice = createSlice({
	name: "auth",
	initialState,
	reducers: {
		setAuthState(state, action) {
			state.authState = action.payload;
		},
		setOpenAuthModal(state, action) {
			state.openAuthModal = true;
			state.openAuthTitle = action?.payload?.title || "Log In";
			state.openAuthMode = action?.payload?.mode || "LOGIN";
		},
		setCloseAuthModal(state) {
			state.openAuthModal = false;
			state.openAuthTitle = "Log In";
			state.openAuthMode = "LOGIN";
		},
		setAuthUserInfo(state, action) {
			state.user = action.payload;
		},
		setIsPendingRegister(state, action) {
			state.isPendingRegister = !!action.payload.value;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(fetchUserInfo.pending, (state) => {
			state.status = "loading";
		});
		builder.addCase(fetchUserInfo.fulfilled, (state, action) => {
			state.status = "idle";
			state.authState = true;
			state.user = action.payload?.data;
		});
		builder.addCase(fetchUserInfo.rejected, (state) => {
			state.status = "idle";
		});
		builder.addCase(fetchUserLogin.fulfilled, (state, action) => {
			let response = action.payload;
			if (response?.user) {
				state.authState = true;
				state.user = { ...response.user };
			}
			if (response?.forceCloseModal) {
				state.openAuthModal = false;
			}
		});
		builder.addCase(fetchUserLogout.fulfilled, (state) => {
			state.authState = false;
			state.user = null;
		});
		builder.addCase(fetchUpdateUserInfo.fulfilled, (state, action) => {
			const response = action.payload;
			if (response && response.data) {
				state.user = { ...response.data };
			}
		});
		builder.addCase(fetchRequestSuiteAccess.fulfilled, (state, action) => {
			const response = action.payload;
			if (response && response.success) {
				state.user = {
					...state.user,
					request_suite_request_status: JET_ONE_SUITE_REQUEST_STATUS.REQUESTING,
				};
			}
		});
	},
});

export const { setAuthState, setOpenAuthModal, setCloseAuthModal, setAuthUserInfo, setIsPendingRegister } =
	authSlice.actions;

export const selectAuthState = (state) => state.auth;

export const selectFormatDateTimeDeparture = (state) => {
	let format = DATETIME_FORMAT_DEPARTURE_12H;
	if (state?.auth?.user?.settings?.time_format === TIME_SETTING_TYPES.TWENTY_FOUR_HOURS_FORMAT) {
		format = DATETIME_FORMAT_DEPARTURE;
	}
	return format;
};

export const selectFormatDateTimeFull = (state) => {
	let format = DATETIME_FORMAT_FULL_12H;
	if (state?.auth?.user?.settings?.time_format === TIME_SETTING_TYPES.TWENTY_FOUR_HOURS_FORMAT) {
		format = DATETIME_FORMAT_FULL;
	}
	return format;
};

export const selectFormatTime = (state) => {
	let format = TIME_FORMAT_12;
	if (state?.auth?.user?.settings?.time_format === TIME_SETTING_TYPES.TWENTY_FOUR_HOURS_FORMAT) {
		format = TIME_FORMAT_24;
	}
	return format;
};

export const isFormatTime12H = (state) => {
	let format = true;
	if (state?.auth?.user?.settings?.time_format === TIME_SETTING_TYPES.TWENTY_FOUR_HOURS_FORMAT) {
		format = false;
	}
	return format;
};

export default authSlice.reducer;
